import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
} from '@mui/material';
import { styled as muiStyled, css, useTheme } from '@mui/material/styles';
import React from 'react';

import type { DrillDownControlItem } from './drill-down-popover';

import { getDesignTokens } from '~/client/components/base/core';
import type { StatusListItem } from '~c/components/base/expandable-status-list';
import Text from '~c/components/base/text';

const DRAWER_WIDTH = 450;

export type DrawerContent =
  | {
      type: 'status-list';
      title: string;
      subtitle?: string;
      items: StatusListItem[];
    }
  | {
      type: 'control-list';
      title: string;
      controls: DrillDownControlItem[];
      uniqueAssessments: number;
    };

interface Props {
  className?: string;
  content: DrawerContent | null;
  // Skeleton for control-list drawers only.
  loading?: boolean;
  // Pagination — control-list drawers only.
  hasMore?: boolean;
  totalCount?: number;
  onLoadMore?: () => void;
  onClose: () => void;
}

const SKELETON_ROW_COUNT = 6;

const DashboardDrillDownDrawerImpl: React.FC<Props> = ({
  className,
  content,
  loading = false,
  hasMore = false,
  totalCount = 0,
  onLoadMore,
  onClose,
}) => {
  const theme = useTheme();
  const t = getDesignTokens(
    theme.palette.mode === 'dark' ? 'dark' : 'light',
    theme.accentPalette ?? 'current'
  );

  const showControlSkeleton =
    loading &&
    content?.type === 'control-list' &&
    content.controls.length === 0;

  const itemCount =
    content?.type === 'status-list'
      ? content.items.length
      : content?.type === 'control-list'
        ? content.controls.length
        : 0;

  const subtitle = content
    ? content.type === 'status-list'
      ? (content.subtitle ??
        `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`)
      : showControlSkeleton
        ? 'Loading…'
        : `across ${content.uniqueAssessments} ${content.uniqueAssessments === 1 ? 'assessment' : 'assessments'}`
    : '';

  return (
    <Drawer
      className={className}
      anchor="right"
      variant="temporary"
      open={content !== null}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH,
          backgroundColor: t.surface1,
          borderLeft: `1px solid ${t.borderDefault}`,
          borderRadius: 0,
        },
      }}
    >
      {content && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box
            sx={{
              px: 3,
              py: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.03)'
                  : 'rgba(0,0,0,0.02)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 1,
                  minWidth: 0,
                }}
              >
                <Box
                  component="span"
                  title={content.title}
                  sx={{
                    display: 'block',
                    fontSize: '11px',
                    fontFamily: "'Nunito Sans', sans-serif",
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: theme.palette.text.secondary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                >
                  {content.title}
                </Box>
                {!showControlSkeleton && (
                  <Text
                    sz="body12"
                    sx={{
                      fontSize: '12px',
                      fontWeight: 700,
                      color: 'text.primary',
                      flexShrink: 0,
                    }}
                  >
                    ({itemCount})
                  </Text>
                )}
              </Box>
              <Box
                component="span"
                title={subtitle}
                sx={{
                  display: 'block',
                  fontSize: '11px',
                  fontFamily: "'Nunito Sans', sans-serif",
                  color: theme.palette.text.disabled,
                  mt: 0.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {subtitle}
              </Box>
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ mt: -0.25, mr: -0.5, color: 'text.secondary' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Scrollable list */}
          <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
            {showControlSkeleton &&
              Array.from({ length: SKELETON_ROW_COUNT }).map((_, idx) => (
                <React.Fragment key={`skeleton-${idx}`}>
                  <Box sx={{ pl: 4, pr: 3, py: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                      }}
                    >
                      <Skeleton
                        variant="circular"
                        width={8}
                        height={8}
                        sx={{ flexShrink: 0, mt: '4px' }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Skeleton
                          variant="text"
                          width="80%"
                          sx={{ fontSize: '13px', lineHeight: 1.4 }}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mt: 0.5,
                          }}
                        >
                          <Skeleton
                            variant="rounded"
                            width={90}
                            height={18}
                            sx={{ borderRadius: '4px' }}
                          />
                          <Skeleton variant="text" width={140} height={14} />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  {idx < SKELETON_ROW_COUNT - 1 && (
                    <Divider sx={{ mx: 3, opacity: 0.3 }} />
                  )}
                </React.Fragment>
              ))}

            {!showControlSkeleton &&
              content.type === 'control-list' &&
              content.controls.length === 0 && (
                <Box sx={{ px: 3, py: 3 }}>
                  <Text
                    sz="body14"
                    sx={{
                      fontSize: '13px',
                      color: theme.palette.text.disabled,
                      fontStyle: 'italic',
                    }}
                  >
                    No controls in this slice.
                  </Text>
                </Box>
              )}

            {content.type === 'status-list' &&
              content.items.map((item, idx) => (
                <React.Fragment key={idx}>
                  <Box
                    onClick={
                      item.onClick
                        ? (e: React.MouseEvent) => {
                            e.stopPropagation();
                            onClose();
                            item.onClick!();
                          }
                        : undefined
                    }
                    role={item.onClick ? 'button' : undefined}
                    tabIndex={item.onClick ? 0 : undefined}
                    onKeyDown={
                      item.onClick
                        ? (e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onClose();
                              item.onClick!();
                            }
                          }
                        : undefined
                    }
                    sx={{
                      pl: 4,
                      pr: 3,
                      py: 1,
                      transition: 'background-color 0.15s ease',
                      ...(item.onClick && {
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255,255,255,0.06)'
                              : 'rgba(0,0,0,0.03)',
                        },
                      }),
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          flexShrink: 0,
                          backgroundColor: 'text.disabled',
                          mt: '4px',
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          component="span"
                          title={item.name}
                          sx={{
                            display: 'block',
                            fontSize: '13px',
                            fontFamily: "'Nunito Sans', sans-serif",
                            lineHeight: 1.4,
                            fontWeight: 500,
                            color: theme.palette.text.primary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                          }}
                        >
                          {item.name}
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mt: 0.5,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Chip
                            label={item.status}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '9px',
                              fontWeight: 700,
                              letterSpacing: '0.04em',
                              textTransform: 'uppercase',
                              color: '#fff',
                              backgroundColor: item.color,
                              borderRadius: '4px',
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                          {item.onClick && (
                            <ChevronRightIcon
                              sx={{
                                fontSize: 12,
                                color: 'text.disabled',
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  {idx < content.items.length - 1 && (
                    <Divider sx={{ mx: 6, opacity: 0.5 }} />
                  )}
                </React.Fragment>
              ))}

            {content.type === 'control-list' &&
              content.controls.map((control, idx) => (
                <React.Fragment
                  key={`${control.assessmentId}-${control.controlIdentifier}-${idx}`}
                >
                  <Box
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      onClose();
                      control.onAssessmentClick();
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onClose();
                        control.onAssessmentClick();
                      }
                    }}
                    sx={{
                      pl: 4,
                      pr: 3,
                      py: 1,
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease',
                      '&:hover': {
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.06)'
                            : 'rgba(0,0,0,0.03)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          flexShrink: 0,
                          backgroundColor: 'text.disabled',
                          mt: '4px',
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          component="span"
                          title={control.controlName}
                          sx={{
                            display: 'block',
                            fontSize: '13px',
                            fontFamily: "'Nunito Sans', sans-serif",
                            lineHeight: 1.4,
                            fontWeight: 500,
                            color: theme.palette.text.primary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                          }}
                        >
                          {control.controlName}
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mt: 0.5,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Chip
                            label={control.status}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '9px',
                              fontWeight: 700,
                              letterSpacing: '0.04em',
                              textTransform: 'uppercase',
                              color: '#fff',
                              backgroundColor: control.color,
                              borderRadius: '4px',
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.25,
                            }}
                          >
                            <Text
                              sz="body12"
                              sx={{
                                fontSize: '10px',
                                color: 'text.disabled',
                                lineHeight: 1.3,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              in: {control.assessmentName}
                            </Text>
                            <ChevronRightIcon
                              sx={{
                                fontSize: 12,
                                color: 'text.disabled',
                                flexShrink: 0,
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {idx < content.controls.length - 1 && (
                    <Divider sx={{ mx: 3, opacity: 0.3 }} />
                  )}
                </React.Fragment>
              ))}

            {/* Pagination footer: "Load more" or "End of list" sentinel. */}
            {!showControlSkeleton &&
              content.type === 'control-list' &&
              content.controls.length > 0 &&
              (hasMore ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    px: 3,
                    py: 2,
                  }}
                >
                  <Button
                    onClick={onLoadMore}
                    disabled={loading || !onLoadMore}
                    size="small"
                    variant="outlined"
                    sx={{ textTransform: 'none' }}
                  >
                    {`Load more (${Math.max(
                      0,
                      totalCount - content.controls.length
                    )} remaining)`}
                  </Button>
                </Box>
              ) : (
                <Box sx={{ px: 3, py: 2, textAlign: 'center' }}>
                  <Text
                    sz="body12"
                    sx={{
                      fontSize: '11px',
                      color: theme.palette.text.disabled,
                      fontStyle: 'italic',
                    }}
                  >
                    End of list
                  </Text>
                </Box>
              ))}
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

const DashboardDrillDownDrawer = muiStyled(DashboardDrillDownDrawerImpl)(
  () => css``
);

export default DashboardDrillDownDrawer;
